import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box, Avatar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { red } from '@mui/material/colors';
import { useNotifications } from 'shared/hooks';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 'fit-content',
  },
  avatar: {
    cursor: 'pointer',
    height: 150,
    width: 150,
    backgroundColor: 'transparent',
  },
  active: {
    opacity: 1,
  },
  error: {
    opacity: 1,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: red[500],
  },
  disabled: {
    pointerEvents: 'none',
  },
  icon: {
    opacity: 0.1,
    color: theme.palette.text.primary,
    width: '100%',
    height: '100%',
  },
}));

const FileUploader = ({ value, onChange, fileUrl, error, disabled, maxSizeMb }) => {
  const classes = useStyles();
  const [files, setFiles] = useState(value);
  const { notifyError } = useNotifications();
  const { t } = useTranslation();

  const onDropRejected = () => {
    if (maxSizeMb) {
      notifyError(t(`UPLOAD.FILE_TOO_LARGE`).replace('{0}', maxSizeMb));
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxSize: maxSizeMb ? maxSizeMb * 1048576 : undefined,
    accept: 'image/*',
    onDrop: acceptedFiles => {
      const extendedFiles = acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );

      setFiles(extendedFiles);
      onChange(extendedFiles);
    },
    onDropRejected,
  });

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach(file => URL.revokeObjectURL(file.preview));
    },
    [files],
  );

  return (
    <Box>
      <div {...getRootProps({ className: clsx('dropzone', classes.root) })}>
        <input {...getInputProps()} disabled={disabled} />
        <Avatar
          className={clsx({
            [classes.avatar]: true,
            [classes.error]: error,
            [classes.disabled]: disabled,
          })}
          src={files.length ? files[0].preview || URL.createObjectURL(files[0]) : fileUrl ?? ''}
          variant="square"
        >
          {!files.length && !fileUrl && (
            <AddPhotoAlternateIcon
              className={clsx({
                [classes.icon]: true,
                [classes.active]: files.length || fileUrl,
              })}
            />
          )}
        </Avatar>
      </div>
    </Box>
  );
};

FileUploader.defaultProps = {
  value: [],
  fileUrl: '',
  error: false,
  disabled: false,
  maxSizeMb: undefined,
};

FileUploader.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  fileUrl: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  maxSizeMb: PropTypes.number,
};

export default FileUploader;
